import React from "react";
import { View, StyleSheet } from "react-native";
import NtModalInputField from "../../../components/NtModalInputField";
import NtOnboardingEditorialSection from "../NtOnboardingEditorialSection";
import NtOnboardingCountryPicker from "../NtOnboardingCountryPicker";
import NtOnboardingField from "../NtOnboardingField";
import NtCardBrandMark from "../NtCardBrandMark";
import {
  detectCardBrand,
  formatCardNumber,
  formatExpiration,
  formatCvc,
  luhnCheck,
  isExpirationValid,
  isCvcValid,
  CARD_BRANDS,
} from "../../utils/paymentUtils";

function NtOnboardingPaymentStep({ payment, onChange }) {
  const update = (patch) => onChange && onChange({ ...payment, ...patch });

  const brand = detectCardBrand(payment?.cardNumber);
  const cardDigits = (payment?.cardNumber || "").replace(/\D/g, "");
  const cardComplete = cardDigits.length >= 12;
  const cardInvalid = cardComplete && !luhnCheck(cardDigits);

  const expDigits = (payment?.expiration || "").replace(/\D/g, "");
  const expComplete = expDigits.length === 4;
  const expInvalid = expComplete && !isExpirationValid(payment?.expiration);

  const cvcLength = brand && CARD_BRANDS[brand] ? CARD_BRANDS[brand].cvcLength : 3;
  const cvcDigits = (payment?.cvc || "").replace(/\D/g, "");
  const cvcInvalid = cvcDigits.length > 0 && !isCvcValid(payment?.cvc, brand);
  const cvcHint = brand === "amex"
    ? "4 digits on the front of your card, above the card number."
    : "3 digits on the back of your card, near the signature strip.";

  return (
    <View>
      <NtOnboardingEditorialSection
        first
        label="Card details"
        caption="The card we'll charge on the Review step."
      >
        <NtModalInputField
          title="Cardholder name *"
          placeholder="Name as it appears on the card"
          value={payment?.cardholderName || ""}
          onChangeText={(cardholderName) => update({ cardholderName })}
          autoComplete="cc-name"
          textContentType="name"
        />
        <NtOnboardingField
          title="Card number *"
          placeholder="1234 5678 9012 3456"
          value={payment?.cardNumber || ""}
          onChangeText={(text) => update({ cardNumber: formatCardNumber(text) })}
          keyboardType="numeric"
          autoComplete="cc-number"
          textContentType="creditCardNumber"
          renderAction={<NtCardBrandMark brand={brand} />}
          error={cardInvalid ? "That doesn't look like a valid card number." : null}
        />
        <View style={styles.row}>
          <View style={styles.cell}>
            <NtOnboardingField
              title="Expiration *"
              placeholder="MM / YY"
              value={payment?.expiration || ""}
              onChangeText={(text) => update({ expiration: formatExpiration(text) })}
              keyboardType="numeric"
              autoComplete="cc-exp"
              error={expInvalid ? "That date has already passed." : null}
            />
          </View>
          <View style={styles.cell}>
            <NtOnboardingField
              title="CVC *"
              placeholder={brand === "amex" ? "1234" : "123"}
              value={payment?.cvc || ""}
              onChangeText={(text) => update({ cvc: formatCvc(text, brand) })}
              keyboardType="numeric"
              secureTextEntry={true}
              autoComplete="cc-csc"
              maxLength={cvcLength}
              error={cvcInvalid ? `Enter the ${cvcLength}-digit CVC for this card.` : null}
              footer={cvcHint}
            />
          </View>
        </View>
      </NtOnboardingEditorialSection>

      <NtOnboardingEditorialSection
        label="Billing address"
        caption="The address tied to the card you entered."
      >
        <NtModalInputField
          title="Address line 1 *"
          placeholder="123 Main St"
          value={payment?.addressLine1 || ""}
          onChangeText={(addressLine1) => update({ addressLine1 })}
          autoComplete="street-address"
          textContentType="fullStreetAddress"
        />
        <NtModalInputField
          title="Address line 2"
          placeholder="Suite, unit, floor (optional)"
          value={payment?.addressLine2 || ""}
          onChangeText={(addressLine2) => update({ addressLine2 })}
          autoComplete="address-line2"
        />
        <View style={styles.row}>
          <View style={styles.cell}>
            <NtModalInputField
              title="City *"
              placeholder="Miami"
              value={payment?.city || ""}
              onChangeText={(city) => update({ city })}
              autoComplete="address-level2"
              textContentType="addressCity"
            />
          </View>
          <View style={styles.cell}>
            <NtModalInputField
              title="State / Region *"
              placeholder="FL"
              value={payment?.state || ""}
              onChangeText={(state) => update({ state })}
              autoComplete="address-level1"
              textContentType="addressState"
            />
          </View>
        </View>
        <View style={styles.row}>
          <View style={styles.cell}>
            <NtModalInputField
              title="ZIP / Postal code *"
              placeholder="33101"
              value={payment?.zip || ""}
              onChangeText={(zip) => update({ zip })}
              autoComplete="postal-code"
              textContentType="postalCode"
            />
          </View>
          <View style={styles.cell}>
            <NtOnboardingCountryPicker
              value={payment?.country || ""}
              onChange={(country) => update({ country })}
            />
          </View>
        </View>
      </NtOnboardingEditorialSection>
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: "row", gap: 14 },
  cell: { flex: 1 },
});

export default NtOnboardingPaymentStep;
